import React, { useEffect } from 'react';
import { InputLabel, MenuItem } from '@mui/material';
import { useLocales } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { Control, Controller } from 'react-hook-form';
import { markAsRequired } from '../../../../utils/formHelpers';
import FormControl from '../../FormControl';
import Select from '../../Select';
import { enrichTestIds } from '../../TestsIds';
import { EnrichForm } from './Enrich';
import { useRecoilValue } from 'recoil';
import { useData } from '../../../../data-layer';

interface DeepLinkProps {
  control: Control<EnrichForm>;
  isSaving: boolean;
}

export const useStyles = makeStyles()(() => ({
  selector: {
    minWidth: 360
  }
}));

export function DeepLinkPathField({ control, isSaving }: DeepLinkProps): JSX.Element | null {
  const { classes } = useStyles();
  const { t } = useLocales();

  const {
    pages: {
      state: { withAllRecords, withIsFetching },
      hook: { getAll }
    }
  } = useData();

  const allPages = useRecoilValue(withAllRecords);
  const isFetchingPages = useRecoilValue(withIsFetching);

  useEffect(() => {
    getAll();
  }, []);

  const pagesCloned = [...(allPages ?? [])];
  const sortedPages = pagesCloned.sort((a, b) => a.urlPath.localeCompare(b.urlPath));

  return (
    <Controller
      control={control}
      rules={{ required: t('general.field_is_required') }}
      name="redirection.value"
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormControl>
          <InputLabel id="typeSelector">{markAsRequired(t('content.drawer.deep_link.url_path'), true)}</InputLabel>
          <Select
            label={markAsRequired(t('content.drawer.deep_link.url_path'), true)}
            className={classes.selector}
            required
            labelId="typeSelector"
            value={value}
            onChange={({ target: { value } }) => {
              onChange(value as string);
            }}
            data-testid={enrichTestIds.deepLinkSelector}
            error={!!error}
            disabled={isSaving}
          >
            {isFetchingPages ? (
              <MenuItem value="">{t('content.drawer.deep_link.url_path_loading')}</MenuItem>
            ) : (
              sortedPages.map((page) => (
                <MenuItem
                  key={page.urlPath}
                  value={page.urlPath}
                  data-testid={enrichTestIds.deepLinkSelectorItem}
                  data-treatment-id={page.urlPath}
                >
                  {page.urlPath}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      )}
    />
  );
}
